import axios from 'axios';
import type {
  LoginResponse,
  User,
  Customer,
  PensionFund,
  Task,
  CustomerActivity,
  StatusOptions,
  PaginatedResponse,
  Intervention,
  Caisse,
  DashboardStats,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/login', { email, password }),

  logout: () => api.post('/logout'),

  me: () => api.get<{ user: User }>('/me'),
};

// Companies API
export const companiesApi = {
  getAll: (params?: { all?: boolean }) => api.get<{ data: Array<{ id: number; name: string }> }>('/companies', params ? { params } : undefined),
};

// Fondations API
export const fondationsApi = {
  getAll: () => api.get<{ data: Array<{ id: number; name: string }> }>('/fondations'),
  getForCompany: (companyId: number) => api.get<{ data: string[] }>(`/fondations/company/${companyId}`),
};

// Customers API
export const customersApi = {
  getAll: (params?: {
    search?: string;
    state?: number;
    company?: number;
    fondation?: string;
    sub_status?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Customer>>('/customers', { params }),

  getOne: (uuid: string) => api.get<{ data: Customer }>(`/customers/${uuid}`),

  create: (data: Partial<Customer>) => api.post<{ data: Customer }>('/customers', data),

  update: (uuid: string, data: Partial<Customer>) =>
    api.put<{ data: Customer }>(`/customers/${uuid}`, data),

  delete: (uuid: string) => api.delete(`/customers/${uuid}`),

  getDetails: (uuid: string) => api.get<{ data: Customer }>(`/customers/${uuid}/details`),

  updateInfo: (uuid: string, data: Partial<Customer>) =>
    api.put<{ data: Customer }>(`/customers/${uuid}/info`, data),

  updateState: (uuid: string, state: number, reason?: string) =>
    api.put<{ data: Customer }>(`/customers/${uuid}/state`, { state, reason }),

  submitAeis: (uuid: string) =>
    api.post<{ success: boolean; message: string; demande_suppletive?: string; screenshot?: string | null; debug?: string[] }>(`/customers/${uuid}/submit-aeis`, {}, { timeout: 180000 }),

  sendCentraleEmail: (uuid: string) =>
    api.post<{ success: boolean; message: string }>(`/customers/${uuid}/send-centrale-email`),

  previewMergedDocument: (uuid: string) =>
    api.get(`/customers/${uuid}/preview-merged-document`, { responseType: 'blob' }),

  updateSubStatus: (uuid: string, subStatus: string) =>
    api.put<{ data: Customer }>(`/customers/${uuid}/sub-status`, { subStatus }),

  updateOnboarding: (uuid: string, onboarding: string) =>
    api.put<{ data: Customer }>(`/customers/${uuid}/onboarding`, { onboarding }),

  uploadDocument: (uuid: string, formData: FormData) =>
    api.post<{ data: Customer }>(`/customers/${uuid}/document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getHistory: (uuid: string) =>
    api.get<{ data: CustomerActivity[] }>(`/customers/${uuid}/history`),

  updateFlowValidation: (uuid: string, data: { centrale_flow_processed?: boolean; suppletive_flow_processed?: boolean }) =>
    api.patch<{ message: string; centrale_flow_processed: boolean; suppletive_flow_processed: boolean }>(`/customers/${uuid}/flow-validation`, data),
};

// Pension Funds API
export const pensionFundsApi = {
  getAll: (params?: {
    search?: string;
    status?: string;
    fondation?: string;
    toDo?: number;
    min_amount?: number;
    max_amount?: number;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<PensionFund>>('/pension-funds', { params }),

  getOne: (uuid: string) => api.get<{ data: PensionFund }>(`/pension-funds/${uuid}`),

  create: (data: Partial<PensionFund>) =>
    api.post<{ data: PensionFund }>('/pension-funds', data),

  update: (uuid: string, data: Partial<PensionFund>) =>
    api.put<{ data: PensionFund }>(`/pension-funds/${uuid}`, data),

  delete: (uuid: string) => api.delete(`/pension-funds/${uuid}`),

  getDetails: (uuid: string) => api.get<{ data: PensionFund }>(`/pension-funds/${uuid}/details`),

  updateStatus: (uuid: string, status: string, reason?: string, additionalData?: {
    amount?: number;
    exit_statement_date?: string;
    commission_amount?: number;
    commissioned?: boolean;
    regroupement_fondation?: string;
    reference?: string;
  }) =>
    api.put<{ data: PensionFund }>(`/pension-funds/${uuid}/status`, {
      status,
      reason,
      ...additionalData,
    }),

  uploadDecompte: (uuid: string, file: File) => {
    const formData = new FormData();
    formData.append('decompte', file);
    return api.post<{ data: PensionFund }>(`/pension-funds/${uuid}/decompte`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  updateDecompteDate: (uuid: string, exit_statement_date: string) =>
    api.put<{ message: string }>(`/pension-funds/${uuid}/decompte-date`, { exit_statement_date }),

  uploadInstruction: (uuid: string, files: { instruction_lemania?: File; instruction_banque?: File }) => {
    const formData = new FormData();
    if (files.instruction_lemania) formData.append('instruction_lemania', files.instruction_lemania);
    if (files.instruction_banque) formData.append('instruction_banque', files.instruction_banque);
    return api.post<{ data: PensionFund }>(`/pension-funds/${uuid}/instruction`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadDemandeTransfert: (uuid: string, file: File) => {
    const formData = new FormData();
    formData.append('demande_transfert', file);
    return api.post<{ data: PensionFund }>(`/pension-funds/${uuid}/demande-transfert`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  generateDemandeTransfert: (uuid: string) =>
    api.post<{ message: string; pdf_url?: string }>(`/pension-funds/${uuid}/generate-demande-transfert`),

  deleteDocument: (uuid: string, documentType: string) =>
    api.delete<{ message: string }>(`/pension-funds/${uuid}/document`, {
      data: { document_type: documentType },
    }),

  updateCommission: (uuid: string, data: { comissione?: string; montant_comission?: number }) =>
    api.put<{ message: string }>(`/pension-funds/${uuid}/commission`, data),

  updateAmount: (uuid: string, amount: number) =>
    api.put<{ data: PensionFund }>(`/pension-funds/${uuid}/amount`, { amount }),

  validateTransfer: (uuid: string) =>
    api.post<{ message: string }>(`/pension-funds/${uuid}/validate`),

  invalidateTransfer: (uuid: string, reason: string) =>
    api.post<{ message: string }>(`/pension-funds/${uuid}/invalidate`, { reason }),

  signInstruction: (uuid: string, fondationName: string) =>
    api.post<{ message: string; file_path?: string }>(`/pension-funds/${uuid}/sign-instruction`, { fondation_name: fondationName }),

  submitUebertrag: (uuid: string, fondation?: string, reference?: string) =>
    api.post<{ success: boolean; message: string; demande_transfert?: string; screenshot?: string | null; screenshots?: Record<string, string> | null; debug?: string[] }>(`/pension-funds/${uuid}/submit-uebertrag`, { fondation, reference }, { timeout: 180000 }),

  generateCaisseInstruction: (uuid: string, fondation?: string) =>
    api.post<{ message: string; file_path?: string }>(`/pension-funds/${uuid}/generate-caisse-instruction`, { fondation }),

  generateFondationInstruction: (uuid: string, fondationName: string) =>
    api.post<{ message: string; file_path?: string }>(`/pension-funds/${uuid}/generate-fondation-instruction`, { fondation_name: fondationName }),

  resetGeneratedInstruction: (uuid: string, type: 'caisse' | 'fondation' | 'demande_transfert') =>
    api.post<{ message: string }>(`/pension-funds/${uuid}/reset-generated-instruction`, { type }),
};

// Workflow API
export const workflowApi = {
  getTasks: () => api.get<{ data: Task[] }>('/workflow/tasks'),

  getAssignedTasks: (params?: { status?: string; per_page?: number; page?: number }) =>
    api.get('/workflow/assigned-tasks', { params }),

  getTaskCustomers: (taskNumber: number, params?: { search?: string; per_page?: number; page?: number }) =>
    api.get<PaginatedResponse<Customer>>(`/workflow/tasks/${taskNumber}/customers`, { params }),

  assignTask: (data: { task_id: number; customer_id: number; advisor_id: number; notes?: string }) =>
    api.post('/workflow/assign', data),

  getStatusOptions: () => api.get<StatusOptions>('/workflow/status-options'),

  getCustomerWorkflowDetails: (customerUuid: string) =>
    api.get<{ data: Customer }>(`/workflow/customers/${customerUuid}/details`),

  updateCustomerState: (customerUuid: string, data: {
    new_state: string | number;
    new_subStatus?: string;
    commentaire?: string;
  }) => api.put<{ data: Customer }>(`/workflow/customers/${customerUuid}/state`, data),

  updateCustomerSubStatus: (customerUuid: string, data: {
    subStatus: string;
    commentaire?: string;
  }) => api.put<{ data: Customer }>(`/workflow/customers/${customerUuid}/sub-status`, data),

  getCustomerHistory: (customerUuid: string) =>
    api.get<{ data: CustomerActivity[] }>(`/workflow/customers/${customerUuid}/history`),

  getDocumentFlowStatus: (customerUuid: string) =>
    api.get<{
      customer_uuid: string;
      workflow_state: string;
      centrale_flow_complete: boolean;
      suppletive_flow_complete: boolean;
      intervention_conseiller: boolean;
      state_before_intervention: string | null;
    }>(`/workflow/customers/${customerUuid}/document-flow-status`),

  createIntervention: (customerUuid: string, data: {
    intervention_type: string;
    message: string;
  }) => api.post<{ message: string; intervention: Intervention }>(`/workflow/customers/${customerUuid}/interventions`, data),

  respondToIntervention: (interventionId: number, data: {
    response: string;
  }) => api.post<{ message: string; intervention: Intervention }>(`/workflow/interventions/${interventionId}/respond`, data),

  closeIntervention: (interventionId: number) =>
    api.post<{ message: string; intervention: Intervention }>(`/workflow/interventions/${interventionId}/close`),

  getInterventions: (params?: {
    status?: string;
    type?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }) => api.get<{
    data: Intervention[];
    meta: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  }>('/workflow/interventions', { params }),

  submitState3Response: (customerUuid: string, data: {
    response_type: 'centrale' | 'suppletive';
    // Centrale data
    no_caisse?: boolean;
    caisse_ids?: number[];
    // Suppletive data
    amount_category?: 'more' | 'less' | 'zero';
    suppletive_amount?: number;
    has_old_transfert?: boolean;
    old_transfert_caisse_id?: string;
    old_transfert_amount?: number;
  }) => api.post<{ message: string; state?: number }>(`/workflow/customers/${customerUuid}/submit-state3`, data),
};

export const dashboardApi = {
  getStats: () => api.get<DashboardStats>('/dashboard/stats'),
};

// User Management API
export const usersApi = {
  getAll: (params?: {
    search?: string;
    role_id?: number;
    is_active?: boolean;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<User>>('/users', { params }),

  getOne: (id: number) => api.get<{ data: User }>(`/users/${id}`),

  create: (data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    role_id: number;
    code: string;
    first_name: string;
    last_name: string;
    phone?: string;
    is_active?: boolean;
    assigned_states?: number[];
  }) => api.post<{ data: User }>('/users', data),

  update: (id: number, data: {
    name?: string;
    email?: string;
    password?: string;
    password_confirmation?: string;
    role_id?: number;
    code?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    is_active?: boolean;
    assigned_states?: number[];
  }) => api.put<{ data: User }>(`/users/${id}`, data),

  delete: (id: number) => api.delete<{ message: string }>(`/users/${id}`),

  getRoles: () => api.get<{ data: Array<{ id: number; name: string; description: string }> }>('/roles'),
};

// Tools API
export const toolsApi = {
  regenerateDocuments: (uuid: string) =>
    api.post<{ message: string; procuration_url: string; demande_centrale_url: string }>(
      `/tools/regenerate-documents/${uuid}`
    ),
};

// Caisses API
export const caissesApi = {
  getAll: (params?: {
    search?: string;
    per_page?: number;
    page?: number;
  }) => api.get<PaginatedResponse<Caisse>>('/caisses', { params }),

  getOne: (id: number) => api.get<{ data: Caisse }>(`/caisses/${id}`),

  create: (data: FormData) => api.post<{ data: Caisse; message: string }>('/caisses', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  update: (id: number, data: FormData) =>
    api.post<{ data: Caisse; message: string }>(`/caisses/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { _method: 'PUT' }, // Laravel method spoofing for file uploads
    }),

  delete: (id: number) => api.delete<{ message: string }>(`/caisses/${id}`),

  getList: () => api.get<{ data: Array<{ id: number; caisse: string; npa?: string; localite?: string }> }>('/caisses-list'),
};

// Exports API
export const exportsApi = {
  exportCustomers: (data: Record<string, unknown>) =>
    api.post('/exports/customers', data, { responseType: 'blob', timeout: 60000 }),

  exportPensionFunds: (data: Record<string, unknown>) =>
    api.post('/exports/pension-funds', data, { responseType: 'blob', timeout: 60000 }),
};

// Export Presets API
export const exportPresetsApi = {
  list: (entityType?: string) =>
    api.get<{ data: Array<{ id: number; name: string; entity_type: string; filters: Record<string, unknown>; columns: string[] | null }> }>(
      '/export-presets',
      entityType ? { params: { entity_type: entityType } } : undefined
    ),

  save: (data: { name: string; entity_type: string; filters: Record<string, unknown>; columns: string[] | null }) =>
    api.post<{ data: { id: number; name: string; entity_type: string; filters: Record<string, unknown>; columns: string[] | null } }>(
      '/export-presets',
      data
    ),

  delete: (id: number) => api.delete(`/export-presets/${id}`),
};

export default api;
